import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Switch,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Add as AddIcon, Close as CloseIcon, Security as SecurityIcon } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import useSWR, { mutate as globalMutate } from 'swr';
import { Controller, useForm } from 'react-hook-form';
import { tenantSecurity, type SecurityPolicyUpdate } from '@/api/platform';
import { DataTable } from '@/components/DataTable';
import { StatusPill } from '@/components/StatusPill';
import { TenantSelect } from '@/components/TenantSelect';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { PageShell } from '@/layout/PageShell';
import { redactMiddle, relativeTime } from '@/utils/format';
import type { TenantSecurityPolicy } from '@/types/backend';

/**
 * List of {@link TenantSecurityPolicy} rows. Operators upsert from a
 * single dialog that takes:
 *   - tenantId (locked when editing)
 *   - ipAllowlist as one CIDR per line (validated server-side via
 *     CidrMatcher.compile — bad input → 400 with the offending entry)
 *   - rpmLimit (0 = unlimited)
 *   - tokenTtlSeconds (0 = platform default)
 *   - enforce master switch
 *
 * Hard-deleting a policy removes it entirely from Mongo; "no policy" is
 * the default state, equivalent to enforce=false. We confirm the delete
 * via ConfirmDialog so a misclick can't drop a tenant's security model.
 */

export function TenantSecurityPage() {
  const theme = useTheme();
  const { data, error, isLoading } = useSWR(
    '/admin/tenant-security/policies',
    () => tenantSecurity.list(),
  );

  const [editing, setEditing] = useState<TenantSecurityPolicy | null>(null);
  const [creating, setCreating] = useState(false);
  const [pendingRemove, setPendingRemove] = useState<TenantSecurityPolicy | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const refresh = () => globalMutate('/admin/tenant-security/policies');

  const submit = async (form: PolicyForm) => {
    setActionError(null);
    try {
      await tenantSecurity.upsert(form.tenantId, {
        ipAllowlist: form.ipAllowlist.split('\n').map((s) => s.trim()).filter(Boolean),
        rpmLimit: Number(form.rpmLimit) || 0,
        tokenTtlSeconds: Number(form.tokenTtlSeconds) || 0,
        enforce: form.enforce,
        description: form.description || undefined,
      });
      setCreating(false);
      setEditing(null);
      refresh();
    } catch (e) {
      const detail = (e as { response?: { data?: { error?: string; message?: string } } })?.response?.data;
      setActionError(detail?.message ?? detail?.error ?? (e as Error).message ?? 'Save failed.');
    }
  };

  const remove = async () => {
    if (!pendingRemove) return;
    await tenantSecurity.remove(pendingRemove.tenantId);
    refresh();
  };

  return (
    <PageShell
      kicker="PLATFORM · SECURITY"
      title="Tenant Security"
      subtitle="Per-tenant IP allowlist and request rate limit. Enforced at the interceptor layer on every request — IP miss → 403, RPM excess → 429."
      width="wide"
      actions={
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreating(true)}
          sx={{ bgcolor: theme.palette.brand.main, '&:hover': { bgcolor: theme.palette.brand.dark } }}
        >
          New policy
        </Button>
      }
    >
      <DataTable<TenantSecurityPolicy>
        rows={data}
        rowKey={(r) => r.id}
        isLoading={isLoading}
        error={error ? { message: (error as Error).message } : null}
        searchable
        searchPlaceholder="tenant id, description…"
        paginated
        empty={{
          icon: SecurityIcon,
          title: 'No policies yet.',
          description: 'Without a policy, every tenant accepts traffic from any source IP at unlimited RPM. Add policies as tenants onboard.',
          action: <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>New policy</Button>,
        }}
        columns={[
          {
            key: 'tenant',
            header: 'Tenant',
            sortable: true,
            sortValue: (r) => r.tenantId,
            filter: { type: 'select', getFilterValue: (r) => r.tenantId },
            pinned: true,
            cell: (r) => <Typography variant="body2" fontWeight={500}>{redactMiddle(r.tenantId, 8, 8)}</Typography>,
            width: 200,
          },
          {
            key: 'enforced',
            header: 'Enforced',
            sortable: true,
            sortValue: (r) => (r.enforce ? 1 : 0),
            filter: {
              type: 'select',
              options: [{ label: 'Yes', value: 'yes' }, { label: 'No', value: 'no' }],
              getFilterValue: (r) => (r.enforce ? 'yes' : 'no'),
            },
            cell: (r) => (r.enforce ? <StatusPill tone="success">Yes</StatusPill> : <StatusPill tone="mono">No</StatusPill>),
            width: 110,
          },
          {
            key: 'ips',
            header: 'IP allowlist',
            sortable: true,
            sortValue: (r) => r.ipAllowlist?.length ?? 0,
            cell: (r) => (
              <Typography variant="caption" color="text.secondary" sx={{ fontFamily: theme.typography.fontFamily }}>
                {r.ipAllowlist && r.ipAllowlist.length > 0 ? `${r.ipAllowlist.length} entries` : 'Any IP'}
              </Typography>
            ),
            width: 130,
          },
          {
            key: 'rpm',
            header: 'Rate limit',
            sortable: true,
            sortValue: (r) => r.rpmLimit,
            cell: (r) => (
              <Typography variant="caption" color="text.secondary">
                {r.rpmLimit > 0 ? `${r.rpmLimit.toLocaleString()} rpm` : 'Unlimited'}
              </Typography>
            ),
            width: 130,
          },
          {
            key: 'updated',
            header: 'Updated',
            sortable: true,
            sortValue: (r) => r.updatedAt,
            cell: (r) => <Typography variant="caption" color="text.secondary">{relativeTime(r.updatedAt)}</Typography>,
            width: 140,
          },
          {
            key: 'actions',
            header: '',
            align: 'right',
            width: 200,
            pinned: true,
            cell: (r) => (
              <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                <Button size="small" variant="text" sx={{ fontSize: 12 }} onClick={() => setEditing(r)}>Edit</Button>
                <Button size="small" variant="text" color="error" sx={{ fontSize: 12 }} onClick={() => setPendingRemove(r)}>
                  Remove
                </Button>
              </Stack>
            ),
          },
        ]}
      />

      {actionError && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      )}

      <PolicyDialog open={creating || !!editing} initial={editing ?? undefined} onSubmit={submit} onClose={() => { setCreating(false); setEditing(null); }} />

      <ConfirmDialog
        open={!!pendingRemove}
        onClose={() => setPendingRemove(null)}
        title="Remove this security policy?"
        body={
          pendingRemove ? (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Tenant <strong>{redactMiddle(pendingRemove.tenantId, 6, 6)}</strong> will fall back to no policy — any source IP, unlimited RPM.
              </Typography>
              <Typography variant="caption" color="text.tertiary">
                This is a hard delete. Re-add the policy to restore enforcement.
              </Typography>
            </>
          ) : null
        }
        actionLabel="Remove policy"
        tone="danger"
        onConfirm={remove}
      />
    </PageShell>
  );
}

interface PolicyForm {
  tenantId: string;
  ipAllowlist: string;
  rpmLimit: number | string;
  tokenTtlSeconds: number | string;
  enforce: boolean;
  description: string;
}

function PolicyDialog({
  open, initial, onSubmit, onClose,
}: {
  open: boolean;
  initial?: TenantSecurityPolicy;
  onSubmit: (form: PolicyForm) => Promise<void>;
  onClose: () => void;
}) {
  const theme = useTheme();
  const { control, handleSubmit, reset, formState: { isSubmitting } } = useForm<PolicyForm>();

  useEffect(() => {
    if (open) {
      reset({
        tenantId: initial?.tenantId ?? '',
        ipAllowlist: (initial?.ipAllowlist ?? []).join('\n'),
        rpmLimit: initial?.rpmLimit ?? 0,
        tokenTtlSeconds: initial?.tokenTtlSeconds ?? 0,
        enforce: initial?.enforce ?? true,
        description: initial?.description ?? '',
      });
    }
  }, [open, initial, reset]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: theme.radius.lg + 'px' } }}>
      <DialogTitle sx={{ pr: 6, fontWeight: 600 }}>
        {initial ? 'Edit security policy' : 'New security policy'}
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box component="form" id="policy-form" onSubmit={handleSubmit(onSubmit)}>
          <Stack spacing={2}>
            <Controller
              name="tenantId"
              control={control}
              rules={{ required: true }}
              render={({ field }) => (
                <TenantSelect
                  value={field.value}
                  onChange={field.onChange}
                  label="Tenant"
                  required
                  disabled={!!initial || isSubmitting}
                  fullWidth
                  allowAll={false}
                  helperText="One policy per tenant. tenantId is the unique key."
                />
              )}
            />
            <Controller
              name="ipAllowlist"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="IP allowlist"
                  multiline
                  minRows={4}
                  fullWidth
                  placeholder={'10.0.0.0/8\n203.0.113.5'}
                  helperText="One CIDR or bare IP per line. Empty = allow any IP. Validated server-side via CidrMatcher."
                  inputProps={{ style: { fontFamily: 'JetBrains Mono, monospace', fontSize: 12 } }}
                />
              )}
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <Controller
                name="rpmLimit"
                control={control}
                render={({ field }) => (
                  <TextField {...field} type="number" label="Rate limit (req/min)" fullWidth helperText="0 = unlimited" />
                )}
              />
              <Controller
                name="tokenTtlSeconds"
                control={control}
                render={({ field }) => (
                  <TextField {...field} type="number" label="Token TTL (sec)" fullWidth helperText="0 = platform default" />
                )}
              />
            </Box>
            <Controller
              name="description"
              control={control}
              render={({ field }) => <TextField {...field} label="Description (optional)" fullWidth />}
            />
            <Controller
              name="enforce"
              control={control}
              render={({ field }) => (
                <Stack direction="row" alignItems="center" spacing={1.5}>
                  <Switch checked={!!field.value} onChange={(_, v) => field.onChange(v)} />
                  <Box>
                    <Typography variant="body2" fontWeight={500}>Enforce</Typography>
                    <Typography variant="caption" color="text.tertiary">When off, the policy is stored but no requests are blocked.</Typography>
                  </Box>
                </Stack>
              )}
            />
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button type="submit" form="policy-form" variant="contained" disabled={isSubmitting}>
          {initial ? 'Save changes' : 'Create policy'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export type { SecurityPolicyUpdate };
